import clsx from "clsx";

interface FalconLogoProps {
  /** Logo size in pixels (renders as a circular badge of this diameter). */
  size?: number;
  /** Extra className for the wrapper. */
  className?: string;
  /** Decorative — hidden from screen readers when wrapped by a labelled link. */
  decorative?: boolean;
}

/**
 * Minimalist falcon mark for Falkenrehde.
 *
 * A stylised flying silhouette inside a circular badge. Uses the active
 * theme tokens (`--color-primary`, `--color-accent`) so that the logo
 * follows the live theme set by admins.
 */
export function FalconLogo({ size = 44, className, decorative = false }: FalconLogoProps) {
  return (
    <span
      className={clsx(
        "inline-flex items-center justify-center rounded-full shadow-sm select-none",
        className,
      )}
      style={{
        width: size,
        height: size,
        background: "var(--color-primary)",
      }}
      aria-hidden={decorative || undefined}
    >
      <svg
        width={Math.round(size * 0.62)}
        height={Math.round(size * 0.62)}
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        role={decorative ? "presentation" : "img"}
        aria-label={decorative ? undefined : "Falkenrehde — Falke"}
      >
        {/* Left wing */}
        <path
          d="M32 32 C 22 18, 14 14, 6 18 C 14 22, 20 28, 26 36 Z"
          fill="#F7F5F0"
        />
        {/* Right wing */}
        <path
          d="M32 32 C 42 18, 50 14, 58 18 C 50 22, 44 28, 38 36 Z"
          fill="#F7F5F0"
        />
        {/* Body */}
        <path
          d="M28 30 L 32 24 L 36 30 L 34 46 L 30 46 Z"
          fill="#F7F5F0"
        />
        {/* Head */}
        <circle cx="32" cy="22" r="3" fill="#F7F5F0" />
        {/* Beak — accent colour */}
        <path d="M32 22 L 35 21 L 32 24 Z" fill="var(--color-accent)" />
      </svg>
    </span>
  );
}
